// Wave AW.3-React (2026-05-16) — 외부 바이어 공개 가입 (사업자등록증 업로드)
// Wave UX-2.1 Item 1 sweep (2026-05-23) — 4 locale i18n 마이그레이션.
//
// 백엔드: POST /buyer-app/auth/register-public (multer 'business_cert', 5MB, JPG/PNG/PDF)

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { LogoFull } from '../Logo';
import { useT } from '../../i18n/I18nProvider';
import { displayApiError } from './buyerApi';
import DevicePolicyBanner from './DevicePolicyBanner';

// labelKey 패턴 — t() 호출 (Wave UX-2.1 Item 1 sweep, 2026-05-23)
var COUNTRY_OPTIONS = [
  { code: 'KR', labelKey: 'countryKR' },
  { code: 'JP', labelKey: 'countryJP' },
  { code: 'US', labelKey: 'countryUS' },
  { code: 'CN', labelKey: 'countryCN' },
  { code: 'RU', labelKey: 'countryRU' },
  { code: 'AE', labelKey: 'countryAE' },
];

function AuctionBuyerRegister() {
  var t = useT();
  var [email, setEmail] = useState('');
  var [password, setPassword] = useState('');
  var [name, setName] = useState('');
  var [company, setCompany] = useState('');
  var [phoneE164, setPhoneE164] = useState('');
  var [businessNo, setBusinessNo] = useState('');
  var [businessCountry, setBusinessCountry] = useState('KR');
  var [certFile, setCertFile] = useState(null);
  var [error, setError] = useState('');
  var [loading, setLoading] = useState(false);
  var fileInputRef = useRef(null);
  var navigate = useNavigate();

  // Inline validation — 입력이 있는데 형식 부정인 경우만 에러 메시지 표시 (빈 입력은 untouched)
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var PHONE_RE = /^\+[1-9]\d{6,14}$/;

  var emailErr = email && !EMAIL_RE.test(email) ? t('errEmailFormat') : '';
  var passwordErr = password && password.length < 8 ? t('errPasswordLength', { n: password.length }) : '';
  var phoneErr = phoneE164 && !PHONE_RE.test(phoneE164) ? t('errPhoneFormat') : '';
  var businessNoErr = businessNo && businessNo.replace(/[^0-9A-Za-z]/g, '').length < 5 ? t('errBusinessNoShort') : '';

  var canSubmit = email.indexOf('@') > 0 &&
    EMAIL_RE.test(email) &&
    password.length >= 8 &&
    name.trim().length > 0 &&
    businessNo.trim().length > 0 &&
    !phoneErr &&
    !businessNoErr &&
    certFile != null;

  // UX — 버튼 비활성 사유 안내 (사용자가 무엇이 부족한지 즉시 파악)
  var missingHints = [];
  if (!email || !EMAIL_RE.test(email)) missingHints.push(t('missingEmail'));
  if (password.length < 8) missingHints.push(t('missingPassword'));
  if (!name.trim()) missingHints.push(t('missingName'));
  if (!businessNo.trim()) missingHints.push(t('missingBusinessNo'));
  if (!certFile) missingHints.push(t('missingCert'));

  var handleFileSelect = function(e) {
    setError('');
    var f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setError(t('errFileSize5MB'));
      return;
    }
    var allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    if (allowed.indexOf(f.type) < 0) {
      setError(t('errFileTypeJpgPngPdf'));
      return;
    }
    setCertFile(f);
  };

  var handleSubmit = function() {
    setError('');
    if (!canSubmit) return;
    setLoading(true);
    var fd = new FormData();
    fd.append('email', email.trim().toLowerCase());
    fd.append('password', password);
    fd.append('name', name.trim());
    if (company.trim()) fd.append('company', company.trim());
    if (phoneE164.trim()) fd.append('phone_e164', phoneE164.trim());
    fd.append('business_no', businessNo.trim());
    fd.append('business_country', businessCountry);
    fd.append('locale', 'ko');
    fd.append('business_cert', certFile);

    fetch(API + '/buyer-app/auth/register-public', {
      method: 'POST',
      body: fd,
    })
      .then(function(r) { return r.json().then(function(d) { return { status: r.status, body: d }; }); })
      .then(function(res) {
        setLoading(false);
        if (res.body && res.body.success) {
          navigate('/buyer/pending');
          return;
        }
        var code = (res.body && res.body.code) || '';
        if (code === 'BUSINESS_BLACKLISTED') {
          setError(t('errBusinessBlacklisted'));
        } else if (code === 'EMAIL_ALREADY_REGISTERED') {
          setError(t('errEmailExists'));
        } else if (code === 'PHONE_ALREADY_REGISTERED') {
          setError(t('errPhoneExists'));
        } else if (code === 'FILE_TOO_LARGE') {
          setError(t('errFileSize5MB'));
        } else if (code === 'INVALID_FILE_TYPE') {
          setError(t('errFileTypeJpgPngPdf'));
        } else if (code === 'PASSWORD_TOO_SHORT') {
          setError(t('errPasswordTooShort'));
        } else {
          setError(displayApiError(res.body, t, 'errRegisterFailed'));
        }
      })
      .catch(function() { setLoading(false); setError(t('errServerConnect')); });
  };

  // 인풋 스타일 — 에러 시 빨간 보더 + 옅은 빨강 배경
  var makeInputStyle = function(hasErr) {
    return {
      width: '100%', height: 44, padding: '0 14px',
      border: '1.5px solid ' + (hasErr ? '#fca5a5' : '#e2e8f0'),
      borderRadius: 10, fontSize: 14,
      outline: 'none', transition: 'border-color 0.2s',
      backgroundColor: hasErr ? '#fef2f2' : '#f8fafc',      boxSizing: 'border-box',
    };
  };
  var errMsgStyle = {
    fontSize: 12, color: '#dc2626', marginTop: 6,
    display: 'flex', alignItems: 'center', gap: 4,
  };
  var inputStyle = {
    width: '100%', height: 44, padding: '0 14px',
    border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14,
    outline: 'none', transition: 'border-color 0.2s',
    backgroundColor: '#f8fafc',
    boxSizing: 'border-box',
  };
  var labelStyle = {
    display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6,
  };

  return (
    <div className="buyer-root" style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg,#f8fafc 0%,#eff6ff 100%)',
      padding: '40px 20px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -200, right: -200, width: 600, height: 600, background: '#dbeafe', borderRadius: '50%', filter: 'blur(100px)', opacity: 0.4, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -200, left: -200, width: 500, height: 500, background: '#e0f2fe', borderRadius: '50%', filter: 'blur(100px)', opacity: 0.4, pointerEvents: 'none' }} />

      <div style={{ maxWidth: 560, margin: '0 auto', position: 'relative', zIndex: 10 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <LogoFull iconSize={36} fontSize={24} />
          <p style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500, letterSpacing: '0.05em', marginTop: 10 }}>USED CAR AUCTION</p>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: 20, padding: '36px 36px', boxShadow: '0 4px 40px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{t('registerTitle')}</h2>
          <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>{t('registerSubtitle')}</p>

          {/* Wave UX-2.4 #26 — 1기기 정책 안내 (env flag 활성 시만 자동 노출) */}
          <DevicePolicyBanner />

          {error && (
            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#dc2626', fontSize: 16 }}>⚠</span>
              <span style={{ color: '#991b1b', fontSize: 13, fontWeight: 500 }}>{error}</span>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{t('registerEmailLabel')}</label>
            <input type="email" value={email} onChange={function(e){setEmail(e.target.value)}}
              placeholder="buyer@example.com" dir="ltr" autoComplete="email"
              style={makeInputStyle(!!emailErr)}
            />
            {emailErr && <div style={errMsgStyle}><span>⚠</span><span>{emailErr}</span></div>}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{t('registerPasswordLabel')}</label>
            <input type="password" value={password} onChange={function(e){setPassword(e.target.value)}}
              style={makeInputStyle(!!passwordErr)}
            />
            {passwordErr && <div style={errMsgStyle}><span>⚠</span><span>{passwordErr}</span></div>}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{t('registerNameLabel')}</label>
            <input type="text" value={name} onChange={function(e){setName(e.target.value)}}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{t('registerCompanyLabel')}</label>
            <input type="text" value={company} onChange={function(e){setCompany(e.target.value)}}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>{t('registerPhoneLabel')}</label>
            <input type="tel" value={phoneE164} onChange={function(e){setPhoneE164(e.target.value)}}
              placeholder="+821012345678" dir="ltr" autoComplete="tel"
              style={makeInputStyle(!!phoneErr)}
            />
            {phoneErr && <div style={errMsgStyle}><span>⚠</span><span>{phoneErr}</span></div>}
          </div>

          <div style={{ height: 1, background: '#f1f5f9', margin: '8px 0 24px' }} />

          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{t('registerBusinessSection')}</h3>
          <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>{t('registerBusinessHint')}</p>

          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 3 }}>
              <label style={labelStyle}>{t('registerBusinessNoLabel')}</label>
              <input type="text" value={businessNo} onChange={function(e){setBusinessNo(e.target.value)}}
                placeholder="123-45-67890" dir="ltr" style={makeInputStyle(!!businessNoErr)}
              />
              {businessNoErr && <div style={errMsgStyle}><span>⚠</span><span>{businessNoErr}</span></div>}
            </div>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>{t('registerCountryLabel')}</label>
              <select value={businessCountry} onChange={function(e){setBusinessCountry(e.target.value)}}
                style={inputStyle}>
                {COUNTRY_OPTIONS.map(function(o){return <option key={o.code} value={o.code}>{t(o.labelKey)}</option>;})}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>{t('registerCertLabel')}</label>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,application/pdf"
              onChange={handleFileSelect} style={{ display: 'none' }} />
            <button type="button"
              onClick={function(){fileInputRef.current&&fileInputRef.current.click()}}
              style={{
                width: '100%', padding: '14px 16px',
                backgroundColor: certFile ? '#eff6ff' : '#f8fafc',
                border: certFile ? '1.5px solid #3b82f6' : '1.5px dashed #cbd5e1',
                borderRadius: 10, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 12,              }}>
              <span style={{ fontSize: 20, color: certFile?'#3b82f6':'#94a3b8' }}>{certFile?'✓':'📎'}</span>
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: certFile?'#1e3a8a':'#64748b', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {certFile ? certFile.name : t('registerCertPick')}
              </span>
              {certFile && <span style={{ fontSize: 12, color: '#94a3b8' }}>{(certFile.size/1024).toFixed(0)}KB</span>}
            </button>
          </div>

          <button type="button" onClick={handleSubmit} disabled={!canSubmit||loading}
            style={{
              width: '100%', height: 48,
              background: (!canSubmit||loading) ? '#cbd5e1' : 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)',
              color: 'white', border: 'none', borderRadius: 12,
              fontSize: 15, fontWeight: 700, cursor: (!canSubmit||loading)?'not-allowed':'pointer',              transition: 'all 0.2s',
              boxShadow: (!canSubmit||loading) ? 'none' : '0 4px 12px rgba(27,58,92,0.2)',
            }}>
            {loading ? t('registerSubmitting') : t('registerSubmitCta')}
          </button>

          {/* 비활성 사유 안내 — 사용자가 무엇이 부족한지 즉시 표시 */}
          {!canSubmit && !loading && missingHints.length > 0 && (
            <div style={{ marginTop: 10, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 12, color: '#92400e' }}>
              <span style={{ fontWeight: 600 }}>{t('registerMissingFields')}</span>
              {missingHints.join(', ')}
            </div>
          )}

          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>{t('registerHaveAccount')} </span>
            <button type="button" onClick={function(){navigate('/buyer/login')}}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: 13, fontWeight: 700, }}>
              {t('registerLoginCta')}
            </button>
          </div>
        </div>

        <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, marginTop: 24 }}>
          © 2026 AUTODEX. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default AuctionBuyerRegister;
